import React, { createContext, useContext, useState, useEffect } from 'react';
import { themeOptimizer } from '../utils/themeOptimizer';

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // Load theme from localStorage or default to 'dark'
  const [theme, setTheme] = useState<Theme>(() => {
    return themeOptimizer.getCurrentTheme();
  });

  // Use optimized theme switching
  const toggleTheme = () => {
    themeOptimizer.toggleTheme();
    setTheme(themeOptimizer.getCurrentTheme());
  };

  // Sync with theme optimizer changes
  useEffect(() => {
    const currentTheme = themeOptimizer.getCurrentTheme();
    if (currentTheme !== theme) {
      setTheme(currentTheme);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
